import React from 'react';

// Fallback only. isi_ms is always served; a task that silently substituted a
// shorter window would understate the response window the server enforces,
// and a longer one would remove the ladder's only escalation on this game.
const DEFAULT_WINDOW_MS = 1600;

// The letter is the whole stimulus. `trialType` ("go" / "no-go") is NOT on the
// wire — a client trial is exactly {trialIndex, stimulus, isPractice}. The rule
// ("anything but X") is inferred from the letter itself, never from a field
// that doesn't exist.
function StimulusView({ stimulus, compact }) {
  return (
    <span
      style={{
        fontSize: compact ? '36px' : 'clamp(72px, 18vw, 160px)',
        fontWeight: 800,
        lineHeight: 1,
        letterSpacing: '-0.03em',
        color: 'var(--text-primary)',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {stimulus.letter}
    </span>
  );
}

export const goNoGoTask = {
  id: 'go-no-go',
  name: 'Stop / Go',
  pillar: 'attention',

  // Load-bearing. Doing nothing IS the correct answer on an X, so a trial that
  // ended on the press would make every go trial visibly shorter than every
  // no-go trial — and inhibition is then readable from the pacing alone,
  // without looking at the letter. The window is also the served isi_ms, the
  // knob the ladder escalates. Applies in training too: a game solvable by
  // rhythm trains rhythm.
  holdsFullWindow: true,

  instructions: [
    'Letters will appear one at a time.',
    'Press for every letter — except X.',
    'When you see X, do nothing.',
  ],
  targets: [{ id: 'press', label: 'Press', keys: [' '], hint: 'space' }],

  examples: [
    { stimulus: { letter: 'K' }, response: 'Press' },
    { stimulus: { letter: 'X' }, response: 'Do nothing' },
  ],

  // This task has exactly one target (press), so targetId carries no information and is ignored.
  toAnswer: () => ({ pressed: true }),

  // isi_ms is both the display duration and the response window: the ladder
  // escalates by shortening it, and a client that lets a response run over
  // removes the manipulation. target_rate is the proportion of no-go trials
  // and must NEVER reach rendering — telling the participant how often X is
  // coming is exactly the preparation this task measures.
  timing: (params) => ({
    fixationMs: 250,
    // Number(), not a bare read: this value becomes the runner's RT ceiling
    // (windowMs + 250). A string isi_ms would concatenate there rather than add,
    // yielding a ceiling like "1600250" and accepting every late response.
    // ||, not ??: a served 0 is not a real window, it is an instant timeout.
    windowMs: Number(params?.isi_ms) || DEFAULT_WINDOW_MS,
    gapMs: 0,
  }),

  // Used only for practice trials. This computes nothing hidden: the rule is
  // "anything but X", and the letter is on screen.
  practiceExpectation: (stimulus) => (stimulus.letter === 'X' ? null : 'press'),

  StimulusView,
};
